import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, from } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { Node } from '@alfresco/js-api';

import { WkUnassignableDocumentDialogComponent } from '../dialogs/wk-unassignable-document-dialog/wk-unassignable-document-dialog.component';
import { CHECKED_OUT_ASPECT } from '../constants';
import { WkAlfrescoApiService } from './wk-alfresco-api.service';
import { CommonConstants } from '../shared';

@Injectable({
  providedIn: 'root',
})
export class WkReassignTaskService {
  constructor(
    private readonly apiService: WkAlfrescoApiService,
    private readonly matDialog: MatDialog
  ) {}

  public checkCanReassignTask(
    sourceId: string,
    node?: Node
  ): Observable<boolean> {
    const node$ = node ? from([node]) : this.getNode(sourceId);
    return node$.pipe(
      map((resolvedNode) => this.canReassignTask(resolvedNode)),
      tap((canReassign) => {
        if (!canReassign) {
          this.openUnassignableDocumentDialog();
        }
      })
    );
  }

  public checkToCompleteTask(sourceId: string): Observable<boolean> {
    return this.getNode(sourceId).pipe(
      map((node) => !this.isDocumentCheckedOut(node)),
      tap((completed) => {
        if (!completed) {
          this.openUnassignableDocumentDialog(true);
        }
      })
    );
  }

  public openUnassignableDocumentDialog(
    reassign = false
  ): MatDialogRef<WkUnassignableDocumentDialogComponent, void> {
    return this.matDialog.open(WkUnassignableDocumentDialogComponent, {
      data: {
        canReassign: reassign,
      },
    });
  }

  private isDocumentCheckedOut(node: Node): boolean {
    return node.aspectNames.includes(CHECKED_OUT_ASPECT);
  }

  private canReassignTask(node: Node): boolean {
    const isLocked = !!(node as any)?.isLocked;
    const hasWorkingCopyAspect = node?.aspectNames?.includes(
      CommonConstants.workingCopyAspect
    );
    return (
      !isLocked && !hasWorkingCopyAspect && !this.isDocumentCheckedOut(node)
    );
  }

  private getNode(sourceId: string): Observable<Node> {
    return from(this.apiService.nodesApi.getNode(sourceId)).pipe(
      map(({ entry }) => entry)
    );
  }
}
